import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import { useLocation, Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import AuthModal from './AuthModal';
import VideoPlayer from './VideoPlayer';
import { reportErrorSilent } from '../lib/errorReporter';

const ApiDemoSection = lazy(() => import('./ApiDemoSection'));
const IntegrationsSection = lazy(() => import('./IntegrationsSection'));

const DEMO_CAL_URL = 'https://cal.com/nicole-from-layerre-xevgq0';

const HeroSection: React.FC = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [returnUrl, setReturnUrl] = useState<string | undefined>();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  // Check if launch mode is enabled
  const isLaunchMode = process.env.REACT_APP_TURN_ON_LAUNCH === 'true';

  // Check for auth modal trigger from protected routes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('auth') === 'true') {
      // Get return URL from sessionStorage
      const returnUrlFromStorage = sessionStorage.getItem('authReturnUrl');
      setReturnUrl(returnUrlFromStorage || undefined);
      setShowAuthModal(true);
      // Clean up the URL parameter and sessionStorage
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('auth');
      window.history.replaceState({}, '', newUrl.toString());
      sessionStorage.removeItem('authReturnUrl');
    }
  }, [location]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (
        mobileNavRef.current &&
        !mobileNavRef.current.contains(e.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [mobileMenuOpen]);

  // Join waitlist handler
  const joinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setIsSuccess(false);

    try {
      const response = await fetch('/join-waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = (await response.json()) as {
        error?: string;
        success?: boolean;
      };

      if (response.ok) {
        setIsSuccess(true);
        setMessage('🎉 Success! Check your email for a welcome message.');
        setEmail('');
      } else {
        setIsSuccess(false);
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setIsSuccess(false);
      setMessage('Failed to join waitlist. Please try again later.');
      await reportErrorSilent({
        context: 'Joining waitlist',
        error,
        additionalInfo: { email },
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Video opacity and scale animations
  const videoOpacity = useTransform(
    scrollYProgress,
    [0, 0.4, 0.8, 1],
    [0, 0.5, 1, 1]
  );
  // Zoom effect: starts small (0.4) and scales up to full size (1) as user scrolls
  const videoScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.4, 0.85, 1]);

  return (
    <section
      ref={sectionRef}
      className='relative pt-40 sm:pt-32 sm:min-h-[200vh] bg-[linear-gradient(135deg,#ffffff_0%,#fafbfc_38%,#f8f9fa_100%)] sm:bg-watercolor-gradient overflow-visible'
      style={{ position: 'relative' }}
    >
      {/* Logo + nav: one row so mobile menu aligns with logo */}
      <div className='absolute top-8 left-4 right-4 z-20 flex items-center justify-between sm:top-6 sm:left-6 sm:right-6 lg:top-8 lg:left-8 lg:right-8'>
        <Link
          to='/'
          className='flex shrink-0 items-center rounded-md ring-offset-2 ring-offset-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40'
        >
          <img
            src='/layerre-logo.svg'
            alt='Layerre Logo'
            width='5915'
            height='1459'
            className='h-8 w-auto sm:h-12 lg:h-16'
          />
        </Link>

        <nav ref={mobileNavRef} className='flex shrink-0 items-center'>
          <div className='hidden md:flex items-center gap-4 lg:gap-6'>
            <Link
              to='/pricing/'
              className='font-sans text-sm sm:text-base text-slate-700 hover:text-primary-600 transition-colors underline-offset-4 decoration-primary-400/0 hover:decoration-primary-400/60 hover:underline'
            >
              Pricing
            </Link>
            <Link
              to='/blog/'
              className='font-sans text-sm sm:text-base text-slate-700 hover:text-primary-600 transition-colors underline-offset-4 decoration-primary-400/0 hover:decoration-primary-400/60 hover:underline'
            >
              Blog
            </Link>
            <Link
              to='/tutorials/'
              className='font-sans text-sm sm:text-base text-slate-700 hover:text-primary-600 transition-colors underline-offset-4 decoration-primary-400/0 hover:decoration-primary-400/60 hover:underline'
            >
              Tutorials
            </Link>
            <Link
              to='/docs/'
              className='font-sans text-sm sm:text-base text-slate-700 hover:text-primary-600 transition-colors underline-offset-4 decoration-primary-400/0 hover:decoration-primary-400/60 hover:underline'
            >
              Docs
            </Link>
            <Link
              to='/canva-palette/'
              className='font-sans text-sm sm:text-base text-slate-700 hover:text-primary-600 transition-colors underline-offset-4 decoration-primary-400/0 hover:decoration-primary-400/60 hover:underline'
            >
              Free Tools
            </Link>
            <Link
              to='/docs/mcp/'
              className='font-sans text-sm sm:text-base text-slate-700 hover:text-primary-600 transition-colors underline-offset-4 decoration-primary-400/0 hover:decoration-primary-400/60 hover:underline'
            >
              MCP
            </Link>
            <a
              href={DEMO_CAL_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='font-sans text-sm sm:text-base font-medium text-white bg-primary-600 hover:bg-primary-700 px-4 py-2 rounded-full transition-all shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
            >
              Book a demo
            </a>
          </div>

          <div className='relative md:hidden'>
            <button
              type='button'
              id='hero-mobile-nav-toggle'
              aria-expanded={mobileMenuOpen}
              aria-controls='hero-mobile-nav-panel'
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileMenuOpen(open => !open)}
              className='flex h-8 w-8 items-center justify-center rounded-md text-slate-800 hover:bg-slate-900/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50'
            >
              {mobileMenuOpen ? (
                <X className='h-6 w-6' aria-hidden strokeWidth={2} />
              ) : (
                <Menu className='h-6 w-6' aria-hidden strokeWidth={2} />
              )}
            </button>

            {mobileMenuOpen ? (
              <div
                id='hero-mobile-nav-panel'
                role='menu'
                aria-labelledby='hero-mobile-nav-toggle'
                className='absolute right-0 top-full z-30 mt-2 w-[min(100vw-2rem,16rem)] overflow-hidden rounded-xl border border-slate-200/90 bg-white/95 py-2 shadow-xl shadow-slate-900/10 backdrop-blur-md'
              >
                <Link
                  role='menuitem'
                  to='/pricing/'
                  className='block px-4 py-3 font-sans text-base text-slate-700 hover:bg-slate-50 hover:text-primary-600'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <Link
                  role='menuitem'
                  to='/blog/'
                  className='block px-4 py-3 font-sans text-base text-slate-700 hover:bg-slate-50 hover:text-primary-600'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  role='menuitem'
                  to='/tutorials/'
                  className='block px-4 py-3 font-sans text-base text-slate-700 hover:bg-slate-50 hover:text-primary-600'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Tutorials
                </Link>
                <Link
                  role='menuitem'
                  to='/docs/'
                  className='block px-4 py-3 font-sans text-base text-slate-700 hover:bg-slate-50 hover:text-primary-600'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Docs
                </Link>
                <Link
                  role='menuitem'
                  to='/canva-palette/'
                  className='block px-4 py-3 font-sans text-base text-slate-700 hover:bg-slate-50 hover:text-primary-600'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Free Tools
                </Link>
                <Link
                  role='menuitem'
                  to='/docs/mcp/'
                  className='block px-4 py-3 font-sans text-base text-slate-700 hover:bg-slate-50 hover:text-primary-600'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  MCP
                </Link>
                <div className='border-t border-slate-100 px-3 py-3'>
                  <a
                    role='menuitem'
                    href={DEMO_CAL_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex w-full items-center justify-center rounded-full bg-primary-600 px-4 py-2.5 font-sans text-base font-medium text-white hover:bg-primary-700'
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Book a demo
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </nav>
      </div>

      {/* Hero Content Section */}
      <div className='relative z-10 max-w-5xl mx-auto px-4 sm:px-8 lg:px-6 text-center sm:flex sm:items-center sm:min-h-screen sm:-mt-12 lg:-mt-20 pt-8 sm:pt-0'>
        <div className='w-full'>
          {/* Headline */}
          <h1 className='font-header text-4xl max-[360px]:text-[2.15rem] md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 sm:mb-8 leading-[1.08] tracking-tight text-balance px-2 sm:px-8 [text-shadow:0_1px_2px_rgba(255,255,255,0.6)]'>
            <span className='text-slate-800'>Your Canva designs,</span>
            <br />
            <span className='flex w-full justify-center'>
              <span className='whitespace-nowrap inline-flex items-baseline gap-[0.2em] max-[360px]:gap-[0.16em]'>
                <span className='bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent'>
                  automated
                </span>
                <span className='text-slate-800'>and</span>
                <span className='bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent'>
                  scaled
                </span>
              </span>
            </span>
          </h1>

          {/* Explanatory text */}
          <p className='font-sans text-lg md:text-xl text-slate-600 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-2 text-pretty'>
            Take an existing Canva design and generate bulk ads, social posts,
            and banners from a spreadsheet or API.
          </p>

          {/* CTA Section - conditional based on launch mode and auth */}
          {isLaunchMode ? (
            <div className='max-w-md mx-auto px-2'>
              {isAuthenticated ? (
                <Link
                  to='/app/dashboard'
                  className='inline-flex items-center justify-center relative w-full sm:w-auto sm:min-w-[280px] h-14 sm:h-16 text-base sm:text-lg font-semibold text-white shadow-lg shadow-primary-900/15 hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-full px-8 overflow-hidden group bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
                >
                  <span className='relative z-10'>Go to Dashboard →</span>
                  <div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out'></div>
                </Link>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  size='lg'
                  className='relative w-full sm:w-auto sm:min-w-[280px] h-14 sm:h-16 text-base sm:text-lg font-semibold text-white shadow-lg shadow-primary-900/15 hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-full px-8 overflow-hidden group bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
                >
                  <span className='relative z-10'>
                    Start automating for free
                  </span>
                  <div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out'></div>
                </Button>
              )}
            </div>
          ) : (
            <div className='max-w-md mx-auto px-2'>
              <div className='rounded-2xl border border-slate-200/60 bg-white/50 p-3 shadow-sm backdrop-blur-sm sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none sm:backdrop-blur-none'>
                <form
                  onSubmit={joinWaitlist}
                  className='flex flex-col sm:flex-row gap-3 mb-4'
                >
                  <Input
                    type='email'
                    placeholder='Enter your email'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className='flex-1 h-12 sm:h-14 text-sm sm:text-base bg-white/95 backdrop-blur-sm border-2 border-primary-200/90 focus:border-primary-400 focus-visible:ring-2 focus-visible:ring-primary-500/45 transition-all duration-200 rounded-full px-6 shadow-inner shadow-slate-900/[0.03]'
                    disabled={isLoading}
                  />
                  <Button
                    type='submit'
                    size='lg'
                    disabled={isLoading || !email.trim()}
                    className='whitespace-nowrap min-w-[160px] h-12 sm:h-14 text-sm sm:text-base rounded-full font-semibold shadow-lg shadow-primary-900/10 hover:shadow-xl cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
                  >
                    {isLoading ? (
                      <div className='flex items-center gap-2'>
                        <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                        <span>Joining...</span>
                      </div>
                    ) : (
                      <span>Join Waitlist</span>
                    )}
                  </Button>
                </form>
              </div>

              {/* Status Message */}
              {message && (
                <div
                  className={`p-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isSuccess
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {message}
                </div>
              )}

              {/* Secondary CTA hint */}
              {!message && (
                <p className='font-sans text-sm text-slate-500 mt-4'>
                  Get product updates & early API access • No spam, ever
                </p>
              )}
            </div>
          )}
        </div>

        {/* Floating cloud mascots */}
        <div className='hidden sm:block absolute top-12 sm:top-16 lg:top-20 -right-8 sm:-right-12 lg:-right-16 xl:-right-20 animate-bounce-slow z-0'>
          <img
            src='/larry-look-left.svg'
            alt='Cloud Mascot 1'
            className='w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48'
          />
        </div>
        <div className='hidden sm:block absolute bottom-12 sm:bottom-16 lg:bottom-20 -left-8 sm:-left-12 lg:-left-16 xl:-left-20 animate-float z-0'>
          <img
            src='/larry-point-right.svg'
            alt='Cloud Mascot 2'
            className='w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48'
          />
        </div>
      </div>

      {/* Mobile-only subtle Larry mascots - positioned relative to section */}
      <div className='sm:hidden absolute top-16 right-4 animate-bounce-slow z-0'>
        <img
          src='/larry-look-left.svg'
          alt='Cloud Mascot 1'
          className='w-24 h-24'
        />
      </div>
      {/* Easy API Text Section */}
      <div className='relative z-10 max-w-4xl mx-auto px-4 sm:px-8 text-center mt-32 sm:mt-24 lg:mt-32'>
        <div className='relative'>
          {/* Mobile bottom cloud - positioned to the left of text */}
          <div className='sm:hidden absolute -left-12 -top-2 animate-float z-0'>
            <img
              src='/larry-point-right.svg'
              alt='Cloud Mascot 2'
              className='w-20 h-20'
            />
          </div>
          <h2 className='font-header text-xl leading-snug sm:leading-relaxed sm:text-3xl md:text-4xl font-bold text-slate-800 pl-12 sm:pl-0'>
            Our API is incredibly simple to use. Just a few lines of code.
          </h2>
        </div>
      </div>

      {/* API Demo Section */}
      <div className='relative z-10 mt-6 sm:mt-12'>
        <Suspense fallback={<div className='h-96' />}>
          <ApiDemoSection />
        </Suspense>
      </div>

      {/* Simple UI Text Section */}
      <div className='relative z-10 max-w-4xl mx-auto px-4 sm:px-8 text-center mt-8 sm:mt-16 lg:mt-20'>
        <h2 className='font-header text-xl leading-snug sm:leading-relaxed sm:text-3xl md:text-4xl font-bold text-slate-800'>
          Our UI makes it even easier. No code required.
        </h2>
      </div>

      {/* Demo Video Section */}
      <div className='relative w-full max-w-6xl lg:max-w-[95vw] mx-auto px-4 sm:px-8 lg:px-8 mt-12 sm:mt-64 lg:mt-16 pb-6 sm:pb-8 lg:pb-10'>
        {/* Video Container */}
        <motion.div
          className='relative z-20 w-full max-w-5xl lg:max-w-[90vw] mx-auto'
          style={{
            opacity: videoOpacity,
            scale: videoScale,
          }}
        >
          <div className='relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-900/10'>
            <VideoPlayer className='w-full h-full' />
          </div>
        </motion.div>
      </div>

      <Suspense fallback={<div className='min-h-[200px]' aria-hidden />}>
        <IntegrationsSection />
      </Suspense>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        returnUrl={returnUrl}
      />
    </section>
  );
};

export default HeroSection;
